import {Box, Flex} from '@indoqa/style-system'
import {useQueryParam} from '@oegbv/ui-shared'
import * as React from 'react'
import {useHistory} from 'react-router'
import {ajax} from 'rxjs/ajax'
import {AdminLayoutContext, AdminLayoutContextValue} from '../../admin/layout/AdminLayoutContext'
import {Button} from '../../common/components/button/Button'
import {Table} from '../../common/components/table/Table'
import {cellForceWrap, cellWidth} from '../../common/components/table/table.utils'
import {TableRow} from '../../common/components/table/TableRow'
import {booksLibraryPath, BOOKS_REQUEST_PARAMS} from '../books.paths'
import {loadBookMappings} from '../state/books.services'
import {BookMapping, BookMappings} from '../state/books.types'

const headers = ['Titel', 'E-Book-ID', 'Webbook-ID', 'Slug']

type Filter = 'WEBBOOK_MAPPED' | 'WEBBOOK_UNMAPPED' | 'SLUG_MISSING' | 'EBOOK_MAPPING_MISSING'

const sortBooks = (bookMapping1?: BookMapping | null, bookMapping2?: BookMapping | null): number => {
  // 1. If both are missing, consider them equal
  if (!bookMapping1 && !bookMapping2) {
    return 0
  }

  // 2. If only one is missing, sort it before/after the other
  if (!bookMapping1) {
    return -1
  }
  if (!bookMapping2) {
    return 1
  }

  // 3. Extract slugs safely, defaulting to empty string if null/undefined
  const slug1 = bookMapping1.ebookTitle ?? ''
  const slug2 = bookMapping2.ebookTitle ?? ''

  // 4. Perform locale-aware comparison
  return slug1.localeCompare(slug2)
}

const getBookTitle = (bookMapping?: BookMapping | null): string => {
  if (!bookMapping) {
    return '-'
  }
  if (bookMapping.ebookTitle) {
    return bookMapping.ebookTitle
  }
  if (bookMapping.webBookTitle) {
    return bookMapping.webBookTitle + ' (webbook)'
  }
  return 'Kein Titel gesetzt'
}

export const BooksLibraryPage = () => {
  const [bookMappingsToc, setBookMappingsToc] = React.useState<BookMappings | null>(null)
  const {setScrollToTop} = React.useContext<AdminLayoutContextValue>(AdminLayoutContext)

  const history = useHistory()
  const filter = useQueryParam(BOOKS_REQUEST_PARAMS.filter) as Filter

  React.useEffect(() => {
    setScrollToTop(new Date())
    loadBookMappings(ajax).subscribe(setBookMappingsToc)
  }, [setScrollToTop])

  const filterBooks = React.useCallback(
    (bookMapping?: BookMapping | null) => {
      if (!bookMapping) {
        return false
      }
      if (!filter) {
        return true
      }
      if (filter === 'WEBBOOK_MAPPED' && bookMapping.webBookId && bookMapping.ebookId) {
        return true
      }
      if (filter === 'WEBBOOK_UNMAPPED' && !bookMapping.webBookId) {
        return true
      }
      if (filter === 'EBOOK_MAPPING_MISSING' && !bookMapping.ebookId) {
        return true
      }
      return !!(filter === 'SLUG_MISSING' && !bookMapping.slug && bookMapping.webBookId)
    },
    [filter]
  )

  const renderRows = () => {
    const bookMappings = bookMappingsToc?.bookMappings?.filter(filterBooks).sort(sortBooks)
    if (!bookMappings || bookMappings.length === 0) {
      return (
        <tr>
          <td>Keine Bücher gefunden</td>
        </tr>
      )
    }
    return (
      <>
        {bookMappings.map((bookMapping, index) => {
          const cellValues = [getBookTitle(bookMapping), bookMapping.ebookId, bookMapping.webBookId, bookMapping.slug]
          const cellFormats = [
            [cellForceWrap()],
            [cellWidth(150)],
            [cellWidth(150), cellForceWrap()],
            [cellWidth(350), cellForceWrap()],
          ]
          return <TableRow key={index} index={index} cellValues={cellValues} cellFormats={cellFormats} />
        })}
      </>
    )
  }

  return (
    <Box p={4}>
      <h3>Bibliothek</h3>
      <Flex py={2} grow={0}>
        <Button name="Alle" selected={!filter} onClick={() => history.push(booksLibraryPath())} />
        <Flex pl={1}>
          <Button
            name="Vollständig"
            selected={filter === 'WEBBOOK_MAPPED'}
            onClick={() => history.push(booksLibraryPath('WEBBOOK_MAPPED'))}
          />
        </Flex>
        <Flex pl={1}>
          <Button
            name="Webbook-Mapping fehlt"
            selected={filter === 'WEBBOOK_UNMAPPED'}
            onClick={() => history.push(booksLibraryPath('WEBBOOK_UNMAPPED'))}
          />
        </Flex>
        <Flex pl={1}>
          <Button
            name="eBook-Mapping fehlt"
            selected={filter === 'EBOOK_MAPPING_MISSING'}
            onClick={() => history.push(booksLibraryPath('EBOOK_MAPPING_MISSING'))}
          />
        </Flex>
        <Flex pl={1}>
          <Button
            name="Webbook-Slug fehlt"
            selected={filter === 'SLUG_MISSING'}
            onClick={() => history.push(booksLibraryPath('SLUG_MISSING'))}
          />
        </Flex>
      </Flex>
      <Box>
        <Table headers={headers}>{renderRows()}</Table>
      </Box>
    </Box>
  )
}
